<template>
    <Overlay :show="showOverlay">
        <b-card :title="$t('Page(CandidateListImport):Component(Text):page-label')">
            <div class="d-flex">
                <v-select
                    class="mb-0 mr-1 w-25"
                    v-model="substanceList"
                    @input="calculateChangeSet"
                    label="title"
                    :placeholder="$t('Page(ProductCard-ConcernElements):Component(Placeholder):select-substance-list')"
                    :options="substanceListOptions"
                />
                <div class="mr-1 w-25">
                    <b-form-datepicker
                        v-model="validFrom"
                        :placeholder="$t('Component(Placeholder):valid-from')"
                        :date-format-options="$store.state.component.datePicker.format"
                    />
                </div>
                <div class="mr-1 w-25">
                    <b-form-file
                        class="mb-0 pb-0"
                        v-model="file"
                        accept=".xlx, .xlsx, .xlt"
                        placeholder="Choose file"
                        @input="calculateChangeSet"
                    />
                </div>
                <b-button
                    variant="primary w-25"
                    @click="importFile"
                >
                    Import
                </b-button>
            </div>
            <Transition>
                <div v-if="changeSet" class="pt-2 justify-content-center align-items-center">
                    <div>
                        <table class="table b-table table-striped table-borderless">
                            <tbody>
                            <tr>
                                <th class="w-50 text-right">{{ $t('Page(CandidateListImport):Component(Text):number-elements-in-list') }}</th>
                                <td>{{ changeSet.numberElementsInList }}</td>
                            </tr>
                            <tr>
                                <th class="w-50 text-right">{{ $t('Page(CandidateListImport):Component(Text):number-elements-in-file') }}</th>
                                <td>{{ changeSet.numberElementsInFile }}</td>
                            </tr>
                            <tr>
                                <th class="w-50 text-right">{{ $t('Page(CandidateListImport):Component(Text):number-of-new-elements') }}</th>
                                <td>{{ changeSet.numberOfNewElements }}</td>
                            </tr>
                            <tr>
                                <th class="w-50 text-right">{{ $t('Page(CandidateListImport):Component(Text):number-of-new-elements-in-list') }}</th>
                                <td>{{ changeSet.numberOfNewElementsInList }}</td>
                            </tr>
                            <tr>
                                <th class="w-50 text-right">{{ $t('Page(CandidateListImport):Component(Text):number-elements-to-update') }}</th>
                                <td>{{ changeSet.numberOfChangedElements }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <HamletTable
                        :fetch="renderTable"
                        :columns="columns"
                        :default-per-page="30"
                        :td-class-provider="getTdClass"
                        :page-options="[10, 20, 30, 50, 100]"
                    >
                        <template #header-label>
                            Diff
                        </template>

                        <template #cell-type="data">
                            <span v-if="data.item.type === 'diff'">Value rewrite</span>
                            <span v-else>Duplication possible</span>
                        </template>

                        <template #cell-ec="data">
                            <span v-if="data.item.type === 'diff'">{{ data.item.existing.ec }}</span>
                            <span v-else>
                                <div>DB: <strong>{{ data.item.existing.ec }}</strong></div>
                                <div>File: <strong>{{ data.item.new.ec }}</strong></div>
                            </span>
                        </template>

                        <template #cell-cas="data">
                            <span v-if="data.item.type === 'diff'">{{ data.item.existing.cas }}</span>
                            <span v-else>
                                <div>DB: <strong>{{ data.item.existing.cas }}</strong></div>
                                <div>File: <strong>{{ data.item.new.cas }}</strong></div>
                            </span>
                        </template>

                        <template #cell-echaId="data">
                            <span v-if="data.item.type === 'diff'">{{ data.item.existing.echaId }}</span>
                            <span v-else>
                                <div>DB: <strong>{{ data.item.existing.echaId }}</strong></div>
                                <div>File: <strong>{{ data.item.new.echaId }}</strong></div>
                            </span>
                        </template>

                        <template #cell-diff="data">
                            <ul>
                                <li
                                    v-if="data.item.type === 'diff'"
                                    v-for="(change, key) in data.item.changes"
                                    class="pb-50"
                                >
                                    <p>{{ getImportFileColumnName(key) }} field has changes</p>
                                    <div>
                                        DB  value: <strong>{{ data.item.existing[key] }}</strong>
                                    </div>
                                    <div>
                                        File value: <strong>{{ data.item.new[key] }}</strong>
                                    </div>
                                </li>
                                <li v-if="data.item.type === 'id-duplicate'">
                                    <div>
                                        <p>Duplication on <strong>{{ data.item.kind }}</strong></p>
                                        <div>
                                            DB Name: <strong>{{ data.item.existing.echaName }}</strong>
                                        </div>
                                        <div>
                                            File value: <strong>{{ data.item.new.echaName }}</strong>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </template>
                    </HamletTable>
                </div>
            </Transition>
        </b-card>
    </Overlay>
</template>

<script>
import Overlay from "@core/components/common/Overlay";
import {BButton, BCard, BFormGroup, BFormFile, BFormDatepicker} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {SubstanceApi, ImportApi} from "@core/api";
import backendResponse from "@core/mixins/ui/backend-response";
import HamletTable from "@core/components/common/HamletTable.vue";

export default {
    mixins: [backendResponse],
    components: {HamletTable, BButton, BCard, vSelect, BFormGroup, BFormFile, BFormDatepicker, Overlay},
    data() {
        return {
            substanceList: null,
            substanceListOptions: [],
            file: null,
            changeSet: null,
            showOverlay: false,
            validFrom: this.$moment(new Date()).format('YYYY-MM-DD'),

            columns: [
                {
                    key: 'type',
                    label: 'Issue type'
                },
                {
                    key: 'ec',
                    label: this.$t('Component(SubstanceTable):column-ec')
                },
                {
                    key: 'cas',
                    label: this.$t('Component(SubstanceTable):column-cas')
                },
                {
                    key: 'echaId',
                    label: 'Echa ID'
                },
                {
                    key: 'diff',
                    label: 'Diff'
                },
            ],
            changes: []
        };
    },
    methods: {
        getTdClass(column, item) {
            if (item.type === 'id-duplicate') {
                return 'table-danger';
            }

            return '';
        },
        getImportFileColumnName(key) {
            if (key === 'casName') {
                return '"Substance CAS Name"';
            }
            if (key === 'echaName') {
                return '"Substance Name"';
            }

            return key;
        },
        renderTable(config, cb) {
            const start = (config.page - 1) * config.perPage;

            cb(
                this.changes.length,
                this.changes.slice(start, start + config.perPage)
            );
        },
        fileChange() {

        },
        calculateChangeSet() {
            this.doUpload(true);
        },
        importFile() {
            this.doUpload(false);
        },
        doUpload(calculateChangeSet) {
            if (this.substanceList && this.file) {
                this.showOverlay = true;
                ImportApi
                    .importCandidateList(
                        this.substanceList.id,
                        this.file,
                        calculateChangeSet,
                        this.$moment(this.validFrom).format('YYYY-MM-DD HH:mm:ss')
                    )
                    .then(r => {
                        this.changeSet = r.data;
                        this.changes = r.data.changes;
                        if (false === calculateChangeSet) {
                            this.showDefaultSuccessMessage('Successfully imported');
                        }
                    })
                    .catch(e => {
                        this.handleBackendError(e);
                    })
                    .finally(() => {
                        this.showOverlay = false;
                    })
                ;
            }
        }
    },
    created() {
        SubstanceApi.lists().then(r => {
            this.substanceListOptions = r.data;
        });
    }
}
</script>
